import { Button } from '@shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/ui/shadcn/dialog';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { cn } from '@shared/utils/utils';
import { useTranslation } from 'react-i18next';
import { useWalletAccountingSyncDetail } from '../../hooks';

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'medium' }).format(d);
};

interface AccountingSyncAttemptsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: number | null;
}

export const AccountingSyncAttemptsModal = ({
  open,
  onOpenChange,
  transactionId,
}: AccountingSyncAttemptsModalProps) => {
  const { t } = useTranslation('accountingSyncModule');
  const { data, isLoading, isError, error, refetch } = useWalletAccountingSyncDetail(open ? transactionId : null);

  const attempts = data?.sync_attempts ?? [];
  const sorted = [...attempts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-4xl w-[calc(100vw-1.5rem)] max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden',
          'bg-card border-card-border text-foreground shadow-lg',
        )}
      >
        <DialogHeader className="px-6 pt-5 pb-4 shrink-0 border-b border-card-border space-y-1">
          <DialogTitle className="text-left text-xl font-semibold tracking-tight pr-10">
            {t('attempts_modal.title')}
          </DialogTitle>
          {transactionId != null && (
            <p className="text-xs text-muted-foreground font-normal text-left">
              {t('attempts_modal.subtitle', { id: transactionId })}
            </p>
          )}
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 max-h-[min(60vh,calc(90vh-12rem))] px-6 py-4">
          {isLoading && <p className="text-sm text-muted-foreground py-6 text-center">{t('attempts_modal.loading')}</p>}

          {isError && (
            <div className="py-6 space-y-3 text-center">
              <p className="text-sm text-destructive">{error?.message ?? t('errors.generic')}</p>
              <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
                {t('buttons.retry')}
              </Button>
            </div>
          )}

          {!isLoading && !isError && sorted.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">{t('detail.attempts_empty')}</p>
          )}

          {!isLoading && !isError && sorted.length > 0 && (
            <div className="rounded-lg border border-card-border bg-card-bg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-card-border hover:bg-transparent bg-card-bg">
                    <TableHead className="text-foreground">{t('attempts.when')}</TableHead>
                    <TableHead className="text-foreground">{t('attempts.success')}</TableHead>
                    <TableHead className="text-foreground">{t('attempts.http')}</TableHead>
                    <TableHead className="text-foreground">{t('attempts.holded_id')}</TableHead>
                    <TableHead className="text-foreground">{t('attempts.error')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((a) => (
                    <TableRow key={a.id} className="border-card-border">
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDateTime(a.created_at)}
                      </TableCell>
                      <TableCell className="text-sm">{a.success ? t('attempts.yes') : t('attempts.no')}</TableCell>
                      <TableCell className="font-mono text-xs">{a.http_status ?? '—'}</TableCell>
                      <TableCell className="max-w-[120px] truncate font-mono text-xs" title={a.holded_entry_id ?? ''}>
                        {a.holded_entry_id ?? '—'}
                      </TableCell>
                      <TableCell className="max-w-[280px] text-xs text-muted-foreground break-words">
                        {a.error_message ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </ScrollArea>

        <div className="shrink-0 border-t border-card-border bg-card-bg/80 px-6 py-4 flex justify-end">
          <Button type="button" variant="default" size="sm" onClick={() => onOpenChange(false)}>
            {t('entry_modal.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
